import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon, exportToExcel,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge, ImportWizard } from './ui';
import {
  useC360List, C360Table, ColumnChooser,
  ViewIcon, EditIcon, DeleteIcon, UploadIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import { RuleCategoryPage } from './RuleCategoryPage';
import { RulesPage } from './RulesPage';

const API_BASE = '/api/workflows';

const ACTIVE_OPTIONS = [
  { value: 'true', label: 'Aktif' },
  { value: 'false', label: 'Pasif' },
];

const COLUMN_FILTER_META = {
  name: { type: 'text', odataField: 'Name' },
  description: { type: 'text', odataField: 'Description' },
  isActive: { type: 'select', odataField: 'IsActive', valueFormat: 'raw', options: ACTIVE_OPTIONS },
};
const SEARCH_FIELDS = ['Name', 'Description'];

const workflowTabs = [
  { key: 'workflows', label: 'Workflow Yönetimi' },
  { key: 'ruleCategories', label: 'Kural Kategorileri' },
  { key: 'rules', label: 'Kural Yönetimi' },
];

const workflowTabStyles = {
  tabBar: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' },
  tab: { minHeight: 36, padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border-color, #d1d5db)', background: 'var(--bg-card, #fff)', color: 'var(--text-secondary, #4b5563)', cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  tabActive: { background: 'var(--accent)', borderColor: 'var(--accent)', color: '#fff' },
};

function getApiMessage(payload, fallbackMessage) {
  if (!payload) return fallbackMessage;
  if (typeof payload === 'string' && payload.trim()) return payload;
  if (payload.Message || payload.message) return payload.Message || payload.message;
  const errorEntries = payload.errors && typeof payload.errors === 'object'
    ? Object.entries(payload.errors)
      .flatMap(([key, values]) => (Array.isArray(values) ? values.map((value) => `${key}: ${value}`) : []))
      .filter(Boolean)
    : [];
  if (errorEntries.length > 0) return errorEntries.join(' | ');
  if (payload.detail || payload.title) return [payload.title, payload.detail].filter(Boolean).join(' - ');
  return fallbackMessage;
}

export function WorkflowManagementPage() {
  usePageMeta('Workflow Yönetimi', 'Workflow ve saha tanımlarını yönetin');
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();

  const [activeDefinition, setActiveDefinition] = useState('workflows');

  const [selected, setSelected] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [importOpen, setImportOpen] = useState(false);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const list = useC360List({
    apiBase: API_BASE,
    columnFilterMeta: COLUMN_FILTER_META,
    searchFields: SEARCH_FIELDS,
    defaultSortField: 'Name',
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData, exportRows,
  } = list;

  const openNew = () => { navigate('/workflows/new'); };

  const openEdit = (row = selected) => {
    if (!row?.id) return;
    navigate(`/workflows/${row.id}`, { state: { row } });
  };

  // #282 kalem → düzenleme modu (WorkflowDetailPage DetailShell'i ?edit=1 ile açar).
  const openEditRow = (row = selected) => {
    if (!row?.id) return;
    navigate(`/workflows/${row.id}?edit=1`, { state: { row } });
  };

  const handleDelete = async () => {
    if (!selected?.id) return;
    try {
      const res = await fetch(`${API_BASE}/${selected.id}`, { method: 'DELETE', headers: authHeaders() });
      let json = null;
      try { json = await res.json(); } catch { json = null; }
      if (!res.ok) throw new Error(getApiMessage(json, `Workflow silinemedi. (HTTP ${res.status})`));
      setDeleteConfirm(false);
      setSelected(null);
      showToast('success', 'Workflow silindi.');
      fetchData();
    } catch (error) {
      setDeleteConfirm(false);
      showToast('error', error.message || 'Workflow silinemedi.');
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('name', {
      header: 'Workflow', meta: { filterType: 'text' },
      cell: (info) => { const v = info.getValue(); return v ? <strong>{v}</strong> : '-'; },
    }),
    columnHelper.accessor('description', { header: 'Açıklama', meta: { filterType: 'text' }, cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('isActive', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => (info.getValue() ? <StatusBadge tone="ok">Aktif</StatusBadge> : <StatusBadge tone="neutral">Pasif</StatusBadge>),
    }),
    columnHelper.display({
      id: 'usage', header: 'Kullanım', enableColumnFilter: false, enableSorting: false,
      cell: ({ row }) => (
        <span style={{ color: 'var(--text-secondary)' }}>
          {(row.original.inputCount ?? 0)} saha | {(row.original.ruleCount ?? 0)} kural | {(row.original.testCaseCount ?? 0)} test
        </span>
      ),
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); openEdit(row.original); }}><ViewIcon /></button>
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Değiştir" aria-label="Değiştir" onClick={(e) => { e.stopPropagation(); openEditRow(row.original); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setDeleteConfirm(true); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canEdit, canDelete]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('Name', 'asc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { field: 'name', headerName: 'Workflow' },
        { field: 'description', headerName: 'Açıklama' },
        { headerName: 'Durum', valueGetter: ({ row }) => (row.isActive ? 'Aktif' : 'Pasif') },
        { field: 'inputCount', headerName: 'Saha Sayısı' },
        { field: 'ruleCount', headerName: 'Kural Sayısı' },
        { field: 'testCaseCount', headerName: 'Test Sayısı' },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      const count = await exportRows(cols, `workflow-yonetimi_${dateStr}`);
      if (count === 0) showToast('error', 'Dışa aktarılacak veri yok');
      else showToast('success', `${count} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  // === İçe aktarma (sadece workflow başlık alanları) ===
  const importPick = (row, keys) => { for (const k of keys) { if (row[k] != null && row[k] !== '') return String(row[k]).trim(); } return ''; };
  const parseBool = (v) => !['hayır', 'hayir', 'false', '0', 'no', 'pasif'].includes(String(v ?? '').trim().toLowerCase());
  const importRowToPayload = (row) => ({
    name: importPick(row, ['Workflow', 'Ad', 'name', 'Name']),
    description: importPick(row, ['Açıklama', 'Aciklama', 'description', 'Description']),
    isActive: parseBool(importPick(row, ['Durum', 'isActive', 'IsActive']) || 'Aktif'),
  });
  const importValidateRow = (p) => (p.name ? null : 'Workflow adı boş');
  const importSubmitRow = async (p) => {
    try {
      const res = await fetch(API_BASE, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ name: p.name, description: p.description, isActive: p.isActive }) });
      if (res.ok) return { ok: true };
      return { ok: false, error: `Sunucu ${res.status}` };
    } catch (e) {
      return { ok: false, error: e.message || 'Network hatası' };
    }
  };
  const downloadImportTemplate = () => {
    exportToExcel([{ Workflow: 'Örnek Workflow', 'Açıklama': 'Açıklama metni', Durum: 'Aktif' }], null, 'workflow_sablon');
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Workflow adı ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel/CSV'den İçe Aktar" aria-label="Excel/CSV'den İçe Aktar" onClick={() => setImportOpen(true)}><UploadIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  const renderWorkflowTabs = () => (
    <div style={workflowTabStyles.tabBar}>
      {workflowTabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          style={{ ...workflowTabStyles.tab, ...(activeDefinition === tab.key ? workflowTabStyles.tabActive : {}) }}
          onClick={() => setActiveDefinition(tab.key)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );

  if (activeDefinition === 'ruleCategories') {
    return (
      <div style={s.container}>
        {renderWorkflowTabs()}
        <RuleCategoryPage />
      </div>
    );
  }

  if (activeDefinition === 'rules') {
    return (
      <div style={s.container}>
        {renderWorkflowTabs()}
        <RulesPage />
      </div>
    );
  }

  return (
    <div style={s.container}>
      {renderWorkflowTabs()}
      <ListPageShell
        title="Workflow Yönetimi"
        subtitle="Workflow ve saha tanımlarını detay sayfasında yönetin."
        actions={canAdd ? <button style={newRecordButtonStyle} onClick={openNew}>+ Yeni Workflow</button> : null}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openEdit}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3></div>
            <div style={s.modalBody}>
              <p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}>
                <strong>{selected?.name}</strong> workflow kaydını silmek istediğinize emin misiniz?
              </p>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={handleDelete}>Evet, Sil</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <ImportWizard
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onComplete={(okCount) => { if (okCount > 0) { showToast('success', `${okCount} workflow içe aktarıldı`); fetchData(); } }}
        title="Workflow İçe Aktar"
        helpText="Zorunlu alan: Workflow. Açıklama opsiyonel. Durum: Aktif/Pasif (boş ise Aktif). Saha tanımları içe aktarmaya dahil değildir."
        rowToPayload={importRowToPayload}
        validateRow={importValidateRow}
        submitRow={importSubmitRow}
        downloadTemplate={downloadImportTemplate}
      />
      <Toast toast={toast} />
    </div>
  );
}
